import * as React from 'react';
import { useCallback, useState } from 'react';
import cn from 'classnames';

import { useTheme } from '../../../theme';
import { CdnIcon } from '@shared/ui';
import Loader from '../../../BaseComponents/Button/components/Loader/Loader';

interface Props {
	onDownload?: () => void | Promise<void>;
	downloadUrl?: string | null;
	allPassengersDownloadUrls?: string[];
	useSingleDownloadUrl?: boolean;
}

const ServiceSummaryDownloadButton: React.FC<Props> = ({
	onDownload,
	downloadUrl,
	allPassengersDownloadUrls,
	useSingleDownloadUrl = true
}) => {
	const { ServiceSummaryOption: css } = useTheme('ServiceSummary');
	const [isLoading, setIsLoading] = useState(false);

	const handleClick = useCallback(
		async (e: React.MouseEvent) => {
			e.preventDefault();

			if (isLoading) {
				return;
			}

			if (onDownload) {
				setIsLoading(true);

				try {
					await onDownload();
				} catch (error) {
					console.error('Error downloading service: ', error);
				} finally {
					setIsLoading(false);
				}

				return;
			}

			const urls = useSingleDownloadUrl ? [downloadUrl] : allPassengersDownloadUrls;

			urls?.forEach(url => {
				if (url) {
					window.open(url, '_blank');
				}
			});
		},
		[isLoading, onDownload, downloadUrl, allPassengersDownloadUrls, useSingleDownloadUrl]
	);

	return (
		<a
			className={cn(css.option__download, { [css.option__download_loading]: isLoading })}
			href="#"
			onClick={handleClick}
			aria-busy={isLoading}
		>
			{isLoading ? (
				<Loader size={16} classes={{ colorPrimary: css.option__download_loader }} />
			) : (
				<CdnIcon icon="Action/Download" />
			)}
		</a>
	);
};

export default ServiceSummaryDownloadButton;
